class BsbNavMenu extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-bsb-nm-overlay]');
    this.mobileToggle = this.querySelector('[data-bsb-nm-mobile-toggle]');
    this.mobileDrawer = this.querySelector('[data-bsb-nm-mobile-drawer]');
    this.mobileBody = this.querySelector('[data-bsb-nm-mobile-body]');
    this.desktopTriggers = this.querySelectorAll('[data-bsb-nm-flyout-trigger]');
    this.desktopToggles = this.querySelectorAll('[data-bsb-nm-flyout-toggle]');
    this.desktopFlyouts = this.querySelectorAll('[data-bsb-nm-flyout]');
    this.closeBtns = this.querySelectorAll('[data-bsb-nm-close]');
    this.mobileAccordionTriggers = this.querySelectorAll('[data-bsb-nm-mobile-accordion-trigger]');
    this.searchTrigger = this.querySelector('[data-bsb-nm-search-trigger]');
    this.searchOpenBtn = this.querySelector('[data-bsb-nm-search-open]');
    this.searchInput = this.querySelector('[data-search-trigger-input]');

    this.mobileSearchOpenBtn = this.querySelector('[data-bsb-nm-mobile-search-open]');
    this.mobileSearchInput = this.querySelector('[data-bsb-nm-mobile-search-input]');
    this.mobileSearchCancel = this.querySelector('[data-bsb-nm-mobile-search-cancel]');

    this.subpanelTriggers = this.querySelectorAll('[data-bsb-nm-subpanel-trigger]');
    this.subpanelBackBtns = this.querySelectorAll('[data-bsb-nm-subpanel-back]');
    this.activeSubpanel = null;
    this.activeSubpanelTrigger = null;

    this.ctaButtons = this.querySelectorAll('.bsb-nm__cta');
    this.stickyCta = this.querySelector('[data-bsb-nm-sticky-cta]');
    this.ctaCounterpart = this.querySelector('[data-bsb-nm-cta-counterpart]');
    this.ctaCounterpartObserver = null;

    this.activeFlyout = null;
    this.activeTrigger = null;
    this.desktopCloseTimeout = null;
    this.openedViaKeyboard = false;

    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleFlyoutFocusOut = this.handleFlyoutFocusOut.bind(this);
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    this.boundUpdateMobileScrollIndicator = this.updateMobileScrollIndicator.bind(this);
  }

  connectedCallback() {
    this.ctaButtons.forEach((cta) => {
      if (cta.dataset.ctaBg) cta.style.setProperty('--bsb-nm-cta-bg', cta.dataset.ctaBg);
      if (cta.dataset.ctaText) cta.style.setProperty('--bsb-nm-cta-text', cta.dataset.ctaText);
    });

    const header = this;
    const resizeObserver = new ResizeObserver(() => {
      const h = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', h + 'px');
      document.documentElement.style.setProperty('--header-dynamic-height', h + 'px');
    });
    resizeObserver.observe(header);

    this.desktopTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', (e) => this.handleDesktopTriggerEnter(e));
      trigger.addEventListener('mouseleave', () => this.scheduleDesktopClose());
    });

    this.desktopToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => this.handleDesktopToggleClick(e));
      toggle.addEventListener('mouseenter', (e) => this.handleDesktopTriggerEnter(e));
      toggle.addEventListener('mouseleave', () => this.scheduleDesktopClose());
    });

    this.desktopFlyouts.forEach((flyout) => {
      flyout.addEventListener('mouseenter', () => this.cancelDesktopClose());
      flyout.addEventListener('mouseleave', () => this.scheduleDesktopClose());

      const tabs = flyout.querySelectorAll('[data-bsb-nm-subtab]');
      tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', () => this.handleSubtabHover(flyout, tab));
      });

      const toggles = flyout.querySelectorAll('[data-bsb-nm-subtab-toggle]');
      toggles.forEach((toggle) => {
        toggle.addEventListener('mouseenter', () => {
          const id = toggle.getAttribute('data-bsb-nm-subtab-toggle');
          this.activateSubtab(flyout, id);
        });

        toggle.addEventListener('click', () => {
          const id = toggle.getAttribute('data-bsb-nm-subtab-toggle');

          if (toggle.getAttribute('aria-expanded') === 'true') {
            this.collapseAllPanels(flyout);
            return;
          }

          this.activateSubtab(flyout, id);
          toggle.setAttribute('data-activated', 'true');

          const panel = this.querySelector(`#bsb-nm-subpanel-${id}`);
          const focusable = panel && panel.querySelector('a[href], button:not([disabled])');
          if (focusable) focusable.focus();
        });
      });
    });

    document.addEventListener('click', this.boundHandleOutsideClick);

    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', () => this.toggleMobile());
    }

    this.closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.closeMobile());
    });

    this.mobileAccordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => this.toggleMobileAccordion(e));
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeDesktop();
        this.closeMobile();
        this.closeSearch();
      });
    }

    if (this.searchOpenBtn && this.searchInput) {
      this.searchOpenBtn.addEventListener('click', () => this.openSearch());
    }

    if (this.mobileSearchOpenBtn && this.mobileSearchInput) {
      this.mobileSearchOpenBtn.addEventListener('click', () => this.openMobileSearch());
    }

    if (this.mobileSearchInput) {
      this.mobileSearchInput.addEventListener('focus', () => this.activateMobileSearch());
      this.mobileSearchInput.addEventListener('input', () => this.updateMobileSearchState());
    }

    if (this.mobileSearchCancel) {
      this.mobileSearchCancel.addEventListener('click', () => this.deactivateMobileSearch());
    }

    this.subpanelTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        this.openSubpanel(trigger.getAttribute('data-bsb-nm-subpanel-trigger'), trigger);
      });
    });

    this.subpanelBackBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.closeSubpanel());
    });

    if (this.mobileBody) {
      this.mobileBody.addEventListener('scroll', this.boundUpdateMobileScrollIndicator, { passive: true });
    }
    window.addEventListener('resize', this.boundUpdateMobileScrollIndicator);

    if (this.stickyCta && this.ctaCounterpart && this.mobileBody) {
      const offset = this.stickyCta.offsetHeight || 66;
      this.ctaCounterpartObserver = new IntersectionObserver(([entry]) => {
        this.mobileDrawer.classList.toggle('is-cta-counterpart-visible', entry.isIntersecting);
      }, { root: this.mobileBody, rootMargin: `0px 0px -${offset}px 0px` });
      this.ctaCounterpartObserver.observe(this.ctaCounterpart);
    }

    document.addEventListener('keydown', this.boundHandleKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundHandleKeyDown);
    document.removeEventListener('click', this.boundHandleOutsideClick);
    window.removeEventListener('resize', this.boundUpdateMobileScrollIndicator);
    if (this.ctaCounterpartObserver) this.ctaCounterpartObserver.disconnect();
  }

  updateMobileScrollIndicator() {
    if (!this.mobileBody || !this.mobileDrawer) return;

    const { scrollTop, scrollHeight, clientHeight } = this.mobileBody;
    const hasOverflow = scrollHeight - clientHeight > 4;
    const atEnd = scrollHeight - clientHeight - scrollTop <= 4;

    this.mobileDrawer.classList.toggle('has-scroll-overflow', hasOverflow);
    this.mobileDrawer.classList.toggle('is-scroll-end', atEnd);
  }

  // ---- Search trigger morph ----

  openSearch() {
    if (!this.searchTrigger || !this.searchInput) return;
    this.closeDesktop();
    this.searchTrigger.classList.add('is-active');
    this.searchInput.hidden = false;
    this.searchInput.focus();
    this.classList.add('is-desktop-open');
  }

  closeSearch() {
    if (!this.searchTrigger || !this.searchInput) return;
    if (!this.searchTrigger.classList.contains('is-active')) return;
    this.searchTrigger.classList.remove('is-active');
    this.searchInput.hidden = true;

    const modal = document.getElementById('search-modal');
    if (modal && modal.classList.contains('active')) {
      modal.classList.remove('active');
      const modalInput = document.getElementById('Search-In-Modal');
      if (modalInput) {
        modalInput.value = '';
        modalInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.searchInput.value = '';
    }

    this.classList.remove('is-desktop-open');
  }

  handleOutsideClick(e) {
    if (this.searchTrigger && !this.searchTrigger.contains(e.target)) {
      this.closeSearch();
    }
    if (!this.contains(e.target)) this.closeDesktop();
  }

  openMobileSearch() {
    if (!this.classList.contains('is-mobile-open')) {
      this.openMobile();
    }
    this.activateMobileSearch();
    this.mobileSearchInput.focus();
  }

  activateMobileSearch() {
    if (!this.mobileSearchInput) return;
    this.classList.add('is-mobile-searching');
    if (this.mobileSearchCancel) this.mobileSearchCancel.hidden = false;
    this.updateMobileSearchState();
  }

  deactivateMobileSearch() {
    if (!this.mobileSearchInput) return;
    this.classList.remove('is-mobile-searching');
    if (this.mobileSearchCancel) this.mobileSearchCancel.hidden = true;
    this.mobileSearchInput.value = '';
    this.mobileSearchInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.mobileSearchInput.blur();
  }

  updateMobileSearchState() {
    const hasValue = this.mobileSearchInput.value.trim().length > 0;
    this.classList.toggle('has-mobile-search-value', hasValue);
  }

  openSubpanel(id, trigger) {
    const panel = this.querySelector(`#bsb-nm-mobile-subpanel-${id}`);
    if (!panel) return;

    if (this.activeSubpanel && this.activeSubpanel !== panel) {
      this.closeSubpanel();
    }

    if (panel.bsbNmCloseTimeout) {
      clearTimeout(panel.bsbNmCloseTimeout);
      panel.bsbNmCloseTimeout = null;
    }

    if (panel.bsbNmWillChangeTimeout) {
      clearTimeout(panel.bsbNmWillChangeTimeout);
      panel.bsbNmWillChangeTimeout = null;
    }

    panel.classList.add('is-visible');
    panel.scrollTop = 0;
    panel.removeAttribute('inert');
    this.activeSubpanel = panel;

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      this.activeSubpanelTrigger = trigger;
    }

    panel.style.willChange = 'transform';

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add('is-active');
      });
    });

    panel.bsbNmWillChangeTimeout = setTimeout(() => {
      panel.style.willChange = '';
      panel.bsbNmWillChangeTimeout = null;
    }, 300);

    const backBtn = panel.querySelector('[data-bsb-nm-subpanel-back]');
    if (backBtn) backBtn.focus({ preventScroll: true });
  }

  closeSubpanel() {
    if (!this.activeSubpanel) return;
    const panel = this.activeSubpanel;

    if (panel.bsbNmWillChangeTimeout) {
      clearTimeout(panel.bsbNmWillChangeTimeout);
      panel.bsbNmWillChangeTimeout = null;
    }
    panel.style.willChange = 'transform';

    panel.classList.remove('is-active');
    panel.setAttribute('inert', '');

    panel.bsbNmCloseTimeout = setTimeout(() => {
      panel.classList.remove('is-visible');
      panel.style.willChange = '';
      panel.bsbNmCloseTimeout = null;
    }, 260);

    if (this.activeSubpanelTrigger) {
      this.activeSubpanelTrigger.setAttribute('aria-expanded', 'false');
      this.activeSubpanelTrigger.focus();
    }

    this.activeSubpanel = null;
    this.activeSubpanelTrigger = null;
  }

  // ---- Helpers ----

  cancelDesktopClose() {
    clearTimeout(this.desktopCloseTimeout);
  }

  getFlyoutFocusable(flyout) {
    return Array.from(
      flyout.querySelectorAll('a[href], button:not([disabled])')
    ).filter((el) => el.offsetParent !== null);
  }

  // ---- Desktop flyout ----

  handleDesktopTriggerEnter(e) {
    if (window.innerWidth < 1024) return;
    this.cancelDesktopClose();
    this.openedViaKeyboard = false;

    const flyoutId = e.currentTarget.getAttribute('data-bsb-nm-flyout-trigger')
      || e.currentTarget.getAttribute('data-bsb-nm-flyout-toggle');
    const flyout = this.querySelector(`[data-bsb-nm-flyout="${flyoutId}"]`);
    this.openFlyout(flyout, e.currentTarget, false);
  }

  handleDesktopToggleClick(e) {
    if (window.innerWidth < 1024) return;
    this.cancelDesktopClose();

    const flyoutId = e.currentTarget.getAttribute('data-bsb-nm-flyout-toggle');
    const flyout = this.querySelector(`[data-bsb-nm-flyout="${flyoutId}"]`);
    if (!flyout) return;

    if (this.activeFlyout === flyout) {
      this.closeDesktop();
    } else {
      this.openedViaKeyboard = true;
      this.openFlyout(flyout, e.currentTarget, false);
      e.currentTarget.setAttribute('data-activated', 'true');
    }
  }

  scheduleDesktopClose() {
    if (window.innerWidth < 1024) return;
    if (this.openedViaKeyboard) return;
    this.cancelDesktopClose();
    this.desktopCloseTimeout = setTimeout(() => {
      this.closeDesktop();
    }, 300);
  }

  syncTriggerState(flyoutId, expanded) {
    if (!flyoutId) return;
    const link = this.querySelector(`[data-bsb-nm-flyout-trigger="${flyoutId}"]`);
    const toggle = this.querySelector(`[data-bsb-nm-flyout-toggle="${flyoutId}"]`);
    if (link) link.setAttribute('aria-expanded', String(expanded));
    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
  }

  openFlyout(flyout, trigger, moveFocus) {
    if (!flyout) return;

    this.cancelDesktopClose();
    this.closeSearch();

    if (this.activeFlyout && this.activeFlyout !== flyout) {
      this.activeFlyout.classList.remove('is-active');
      this.activeFlyout.setAttribute('aria-hidden', 'true');
      this.activeFlyout.removeEventListener('focusout', this.boundHandleFlyoutFocusOut);
      this.syncTriggerState(this.activeFlyout.getAttribute('data-bsb-nm-flyout'), false);
    }

    this.desktopTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.desktopToggles.forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.removeAttribute('data-activated');
    });

    if (trigger) {
      this.activeTrigger = trigger;
    }

    flyout.classList.add('is-active');
    flyout.setAttribute('aria-hidden', 'false');
    this.activeFlyout = flyout;
    this.syncTriggerState(flyout.getAttribute('data-bsb-nm-flyout'), true);
    this.classList.add('is-desktop-open');
    this.resetFlyoutPanel(flyout);

    flyout.addEventListener('focusout', this.boundHandleFlyoutFocusOut);

    if (moveFocus) {
      const focusable = this.getFlyoutFocusable(flyout);
      if (focusable.length > 0) focusable[0].focus();
    }
  }

  closeDesktop() {
    this.cancelDesktopClose();

    if (this.activeFlyout) {
      const flyoutId = this.activeFlyout.getAttribute('data-bsb-nm-flyout');
      this.activeFlyout.classList.remove('is-active');
      this.activeFlyout.setAttribute('aria-hidden', 'true');
      this.activeFlyout.removeEventListener('focusout', this.boundHandleFlyoutFocusOut);
      this.syncTriggerState(flyoutId, false);
      this.activeFlyout = null;
    }

    this.activeTrigger = null;
    this.openedViaKeyboard = false;
    this.desktopTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.desktopToggles.forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.removeAttribute('data-activated');
    });
    this.classList.remove('is-desktop-open');
  }

  handleFlyoutFocusOut() {
    setTimeout(() => {
      const flyout = this.activeFlyout;
      if (!flyout) return;
      const active = document.activeElement;
      if (active && flyout.contains(active)) return;
      if (active === this.activeTrigger) return;
      this.closeDesktop();
    }, 0);
  }

  // ---- Subnav tabs ----

  syncPanelsHeight(flyout) {
    const wrapper = flyout.querySelector('[data-bsb-nm-panels]');
    if (!wrapper) return;
    const active = wrapper.querySelector('.bsb-nm__products.is-active');
    wrapper.style.height = active ? `${active.scrollHeight}px` : '0px';
  }

  resetFlyoutPanel(flyout) {
    const defaultPanel = flyout.querySelector('[data-bsb-nm-default-panel]');
    if (defaultPanel) {
      defaultPanel.classList.add('is-active');
    }

    flyout.querySelectorAll('[data-bsb-nm-subtab]').forEach((t) => t.classList.remove('is-active'));
    flyout.querySelectorAll('[data-bsb-nm-subtab-toggle]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.removeAttribute('data-activated');
    });
    flyout.querySelectorAll('.bsb-nm__products:not([data-bsb-nm-default-panel])').forEach((p) => p.classList.remove('is-active'));

    this.syncPanelsHeight(flyout);
  }

  collapseAllPanels(flyout) {
    const defaultPanel = flyout.querySelector('[data-bsb-nm-default-panel]');
    if (defaultPanel) {
      defaultPanel.classList.remove('is-active');
    }

    flyout.querySelectorAll('[data-bsb-nm-subtab]').forEach((t) => t.classList.remove('is-active'));
    flyout.querySelectorAll('[data-bsb-nm-subtab-toggle]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.removeAttribute('data-activated');
    });
    flyout.querySelectorAll('.bsb-nm__products:not([data-bsb-nm-default-panel])').forEach((p) => p.classList.remove('is-active'));

    this.syncPanelsHeight(flyout);
  }

  handleSubtabHover(flyout, tab) {
    const id = tab.getAttribute('data-bsb-nm-subtab');
    const panel = id && this.querySelector(`#bsb-nm-subpanel-${id}`);
    if (panel) {
      this.activateSubtab(flyout, id);
    } else {
      this.resetFlyoutPanel(flyout);
    }
  }

  activateSubtab(flyout, id) {
    const targetPanel = this.querySelector(`#bsb-nm-subpanel-${id}`);
    if (!targetPanel) {
      this.resetFlyoutPanel(flyout);
      return;
    }

    const defaultPanel = flyout.querySelector('[data-bsb-nm-default-panel]');
    if (defaultPanel) {
      defaultPanel.classList.remove('is-active');
    }

    flyout.querySelectorAll('[data-bsb-nm-subtab]').forEach((t) => {
      t.classList.toggle('is-active', t.getAttribute('data-bsb-nm-subtab') === id);
    });

    flyout.querySelectorAll('[data-bsb-nm-subtab-toggle]').forEach((t) => {
      const isMatch = t.getAttribute('data-bsb-nm-subtab-toggle') === id;
      t.setAttribute('aria-expanded', String(isMatch));
      if (!isMatch) t.removeAttribute('data-activated');
    });

    flyout.querySelectorAll('.bsb-nm__products:not([data-bsb-nm-default-panel])').forEach((p) => {
      p.classList.toggle('is-active', p.id === `bsb-nm-subpanel-${id}`);
    });

    this.syncPanelsHeight(flyout);
  }

  // ---- Mobile ----

  toggleMobile() {
    if (this.classList.contains('is-mobile-open')) {
      this.closeMobile();
    } else {
      this.openMobile();
    }
  }

  openMobile() {
    this.closeSearch();
    this.classList.add('is-mobile-open');
    if (this.mobileToggle) {
      this.mobileToggle.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('overflow-hidden');

    const closeBtn = this.mobileDrawer && this.mobileDrawer.querySelector('[data-bsb-nm-close]');
    if (closeBtn) closeBtn.focus();

    this.updateMobileScrollIndicator();
  }

  closeMobile() {
    this.classList.remove('is-mobile-open');
    if (this.mobileToggle) {
      this.mobileToggle.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('overflow-hidden');

    if (this.classList.contains('is-mobile-searching')) {
      this.deactivateMobileSearch();
    }

    if (this.activeSubpanel) {
      if (this.activeSubpanel.bsbNmCloseTimeout) {
        clearTimeout(this.activeSubpanel.bsbNmCloseTimeout);
        this.activeSubpanel.bsbNmCloseTimeout = null;
      }
      this.activeSubpanel.classList.remove('is-active', 'is-visible');
      this.activeSubpanel.setAttribute('inert', '');
      if (this.activeSubpanelTrigger) this.activeSubpanelTrigger.setAttribute('aria-expanded', 'false');
      this.activeSubpanel = null;
      this.activeSubpanelTrigger = null;
    }

    this.mobileAccordionTriggers.forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      const content = this.getAccordionContent(t);
      if (content) {
        content.classList.remove('is-open');
        content.setAttribute('inert', '');
      }
    });
  }

  // ---- Mobile accordion ----

  getAccordionContent(trigger) {
    // For split items the trigger is inside .bsb-nm__mobile-split,
    // so the accordion content is the next sibling of the parent div.
    if (trigger.closest('.bsb-nm__mobile-split')) {
      return trigger.closest('.bsb-nm__mobile-split').nextElementSibling;
    }
    return trigger.nextElementSibling;
  }

  toggleMobileAccordion(e) {
    const trigger = e.currentTarget;
    const content = this.getAccordionContent(trigger);
    if (!content) return;

    const isOpen = content.classList.contains('is-open');

    // Close all other accordions
    this.mobileAccordionTriggers.forEach((t) => {
      const c = this.getAccordionContent(t);
      if (c && c !== content) {
        c.classList.remove('is-open');
        c.setAttribute('inert', '');
        t.setAttribute('aria-expanded', 'false');
      }
    });

    // Toggle current
    content.classList.toggle('is-open', !isOpen);
    if (isOpen) {
      content.setAttribute('inert', '');
    } else {
      content.removeAttribute('inert');
    }
    trigger.setAttribute('aria-expanded', String(!isOpen));
  }

  // ---- Keyboard ----

  focusAdjacentSubtab(flyout, panelId, direction) {
    const currentId = panelId.replace('bsb-nm-subpanel-', '');
    const items = Array.from(flyout.querySelectorAll('[data-bsb-nm-subtab]'));
    const currentIndex = items.findIndex((el) => el.getAttribute('data-bsb-nm-subtab') === currentId);

    if (direction < 0) {
      const toggle = flyout.querySelector(`[data-bsb-nm-subtab-toggle="${currentId}"]`);
      if (toggle) {
        toggle.focus();
        return true;
      }
      return false;
    }

    const next = items[currentIndex + 1];
    if (next) {
      next.focus();
      return true;
    }

    const cta = flyout.querySelector('.bsb-nm__cta');
    if (cta) {
      cta.focus();
      return true;
    }

    return false;
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      if (this.searchTrigger && this.searchTrigger.classList.contains('is-active')) {
        this.closeSearch();
      } else if (this.classList.contains('is-mobile-searching')) {
        this.deactivateMobileSearch();
      } else if (this.activeSubpanel) {
        this.closeSubpanel();
      } else if (this.classList.contains('is-mobile-open')) {
        const openAccordion = this.querySelector('.bsb-nm__mobile-accordion-content.is-open');
        if (openAccordion) {
          openAccordion.classList.remove('is-open');
          openAccordion.setAttribute('inert', '');
          const prev = openAccordion.previousElementSibling;
          // For split items, the previous sibling is .bsb-nm__mobile-split —
          // find the actual trigger button inside it.
          const trigger = prev && prev.classList.contains('bsb-nm__mobile-split')
            ? prev.querySelector('[data-bsb-nm-mobile-accordion-trigger]')
            : prev;
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        } else {
          this.closeMobile();
        }
      } else if (this.classList.contains('is-desktop-open')) {
        this.closeDesktop();
        if (this.activeTrigger) this.activeTrigger.focus();
      }
    }

    if (e.key === 'Tab') {
      if (this.activeFlyout) {
        const activePanel = this.activeFlyout.querySelector('.bsb-nm__products.is-active:not([data-bsb-nm-default-panel])');
        if (activePanel && activePanel.contains(document.activeElement)) {
          const focusable = Array.from(activePanel.querySelectorAll('a[href], button:not([disabled])'));
          if (focusable.length > 0) {
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (!e.shiftKey && document.activeElement === last) {
              if (this.focusAdjacentSubtab(this.activeFlyout, activePanel.id, 1)) {
                e.preventDefault();
                return;
              }
            }

            if (e.shiftKey && document.activeElement === first) {
              if (this.focusAdjacentSubtab(this.activeFlyout, activePanel.id, -1)) {
                e.preventDefault();
                return;
              }
            }
          }
        }
      }

      if (this.classList.contains('is-mobile-open')) {
        const drawer = this.mobileDrawer;
        if (!drawer) return;

        const focusable = drawer.querySelectorAll(
          'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const visible = Array.from(focusable).filter((el) => el.offsetParent !== null);
        if (visible.length === 0) return;

        const first = visible[0];
        const last = visible[visible.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }
}

customElements.define('bsb-nav-menu', BsbNavMenu);
